import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Documents from './pages/Documents';
import Directory from './pages/Directory';
import Links from './pages/Links';
import Profile from './pages/Profile';
import RequestForm from './pages/RequestForm';
import SendDocument from './pages/SendDocument';
import Calendar from './pages/Calendar';
import MyPTO from './pages/MyPTO';
import PTORequest from './pages/PTORequest';
import PTOApprovals from './pages/PTOApprovals';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import NewsManage from './pages/NewsManage';
import NewsEditor from './pages/NewsEditor';
import VehicleDocs from './pages/VehicleDocs';
import Candidates from './pages/Candidates';
import CandidateDetail from './pages/CandidateDetail';
import NewHires from './pages/NewHires';
import NewHireDetail from './pages/NewHireDetail';
import OnboardingConfig from './pages/OnboardingConfig';
import Locations from './pages/Locations';
import { CircularProgress, Box } from '@mui/material';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directory"
          element={
            <ProtectedRoute>
              <Directory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/links"
          element={
            <ProtectedRoute>
              <Links />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireManager>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations"
          element={
            <ProtectedRoute requireManager>
              <Locations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/request-form"
          element={
            <ProtectedRoute>
              <RequestForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/send-document"
          element={
            <ProtectedRoute>
              <SendDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <Calendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news"
          element={
            <ProtectedRoute>
              <News />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news/:id"
          element={
            <ProtectedRoute>
              <NewsDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news/manage"
          element={
            <ProtectedRoute requireManager>
              <NewsManage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news/create"
          element={
            <ProtectedRoute requireManager>
              <NewsEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news/edit/:id"
          element={
            <ProtectedRoute requireManager>
              <NewsEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vehicle-docs"
          element={
            <ProtectedRoute>
              <VehicleDocs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pto/my-pto"
          element={
            <ProtectedRoute>
              <MyPTO />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pto/request"
          element={
            <ProtectedRoute>
              <PTORequest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pto/approvals"
          element={
            <ProtectedRoute requireManager>
              <PTOApprovals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/candidates"
          element={
            <ProtectedRoute>
              <Candidates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/candidates/:id"
          element={
            <ProtectedRoute>
              <CandidateDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-hires"
          element={
            <ProtectedRoute>
              <NewHires />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-hires/:id"
          element={
            <ProtectedRoute>
              <NewHireDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding-config"
          element={
            <ProtectedRoute requireManager>
              <OnboardingConfig />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;